import { useCallback } from 'react'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { Button, Tooltip } from '@highlight/ui'
import { useIsAudioNoteReconnecting } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useSetSidepanelState, useSidepanelState } from '@renderer/overlay/atoms'
import { trackSidepanelClosed } from '@renderer/overlay/utils/track-sidepanel'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useSetSidepanelHotkeyOpened } from '@renderer/overlay/Sidepanel/atoms'
import {
  useUpdateTopViewContext,
  useViewActions,
  useViewNavigation,
  useViewTab,
} from '@renderer/overlay/Sidepanel/view-atoms'
import {
  isLiveMeetingShowingTranscript,
  isLiveMeetingView,
  MEETING_VIEW_ID,
} from '@renderer/overlay/Sidepanel/components/meeting/meeting-view-id'
import { useLiveMeetingIdentity } from '@renderer/overlay/Sidepanel/components/meeting/use-live-meeting-identity'
import { AudioElapsedTime } from '../../components/audio-elapsed-time'
import { AudioWaveform } from '../../components/audio-waveform'
import { ReconnectingDots } from '../../components/reconnecting-dots'
import { useIsMiniAnchoredLeft } from '../components/mini/side-context'
import { miniPresenceAnchor, miniRevealVariants } from '../components/mini/side-motion'
import { useIsMiniNotched } from '../atoms'
import { useOpenLiveMeetingInSidepanel } from '../use-open-meeting-in-sidepanel'

const waveformVariants: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: { opacity: 1, scale: 1 },
}

const waveformTransition: Transition = {
  duration: 0.18,
  ease: 'easeOut',
}

interface AudioNoteWaveformButtonProps {
  showElapsed: boolean
}

export function AudioNoteWaveformButton({ showElapsed }: AudioNoteWaveformButtonProps) {
  const isNotched = useIsMiniNotched()
  const isReconnecting = useIsAudioNoteReconnecting()
  const isAnchoredLeft = useIsMiniAnchoredLeft()
  const setSidepanelState = useSetSidepanelState()
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()
  const { openView } = useViewActions()
  const updateMeetingContext = useUpdateTopViewContext(MEETING_VIEW_ID)
  const sidepanelState = useSidepanelState()
  const { topView, topEntryKey } = useViewNavigation()
  const storedTab = useViewTab(topEntryKey)
  const liveMeeting = useLiveMeetingIdentity()
  const openLiveMeeting = useOpenLiveMeetingInSidepanel()
  // The notch keeps one shape with or without the time, so the waveform doesn't shift when it hides
  const waveformEdgeSpacing = !showElapsed && !isNotched ? (isAnchoredLeft ? 'mr-1.5' : 'ml-1.5') : undefined

  const handleClick = useCallback(() => {
    const isOpen = sidepanelState === 'open'
    const isOnTranscript = isLiveMeetingShowingTranscript(topView, liveMeeting, storedTab)
    if (!isOpen || !isOnTranscript) {
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'open active meeting',
        source: isNotched ? 'notch' : 'mini',
      })
    }

    if (!isOpen) {
      openLiveMeeting('transcript')
    } else if (isOnTranscript) {
      trackSidepanelClosed('mini')
      setSidepanelState('hidden')
      setHotkeyOpened(false)
    } else if (isLiveMeetingView(topView, liveMeeting)) {
      // Reopening would collapse onto this entry and replace its whole context, closing the chat drawer
      updateMeetingContext((context) => context && { ...context, initialTab: 'transcript' })
    } else {
      openView(MEETING_VIEW_ID, { ...liveMeeting, initialTab: 'transcript' })
    }
  }, [
    sidepanelState,
    topView,
    liveMeeting,
    storedTab,
    isNotched,
    openLiveMeeting,
    setSidepanelState,
    setHotkeyOpened,
    updateMeetingContext,
    openView,
  ])

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            size="sm"
            variant="ghost"
            className={twMerge(
              'flex cursor-pointer items-center gap-3 px-2',
              // The waveform sits on the anchored side, against the notch when notched, so the time appears beyond it
              !isAnchoredLeft && 'flex-row-reverse',
              isNotched && 'h-7 gap-1.5 px-1.5 py-0',
            )}
            onClick={handleClick}
          />
        }
      >
        <AnimatePresence initial={false} mode="popLayout" anchorX={miniPresenceAnchor(isAnchoredLeft)}>
          <motion.span
            key={isReconnecting ? 'reconnecting' : 'waveform'}
            variants={waveformVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={waveformTransition}
          >
            {isReconnecting ? (
              <ReconnectingDots className={waveformEdgeSpacing} />
            ) : (
              <AudioWaveform className={twMerge(waveformEdgeSpacing, isNotched && 'size-4')} />
            )}
          </motion.span>
        </AnimatePresence>
        <AnimatePresence initial={false} mode="popLayout" anchorX={miniPresenceAnchor(isAnchoredLeft)}>
          {showElapsed && (
            <motion.span
              key="elapsed"
              variants={miniRevealVariants(isAnchoredLeft)}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={waveformTransition}
            >
              <AudioElapsedTime />
            </motion.span>
          )}
        </AnimatePresence>
      </Tooltip.Trigger>
      <Tooltip.Content sideOffset={14} className={isReconnecting ? 'whitespace-pre-line' : undefined}>
        {isReconnecting ? 'Network connection lost.\nTrying to reconnect…' : 'View transcript'}
      </Tooltip.Content>
    </Tooltip>
  )
}
